// Профиль: имя, места в рейтингах и проигранные партии (вкладка «Партии», см. player.ts; партия — в плеере replay.ts), устройства аккаунта,
// связывание устройств кодом или QR, отвязка (вкладка «Устройства»). Аккаунт живёт на сервере
// (server/devices.go); устройство говорит за него ключом (deviceKey в outbox.ts). Окно строится из состояния
// prof — после каждого ответа сервера хватает repaintModal.
import qrcode from 'qrcode-generator';
import type { AccountInfo } from '../types.ts';
import { clientId, deviceKey } from './outbox.ts';
import * as OV from './overlays.ts';
import type { ProfileState } from './overlays.ts';
import { openModal, repaintModal, modalOpen, currentModal, ui, st } from './store.ts';
import { loadPushDevices } from './push.ts';
import { markRead } from './inbox.ts';
import { rememberName, syncPresence } from './social.ts';
import { repaintSocial } from './render.ts';
import { loadPlayer, pagePlayer, otherPlayer } from './player.ts';
import { startReplay } from './replay.ts';

export const prof: ProfileState = { tab: 'games', player: { data: null, loading: false, error: false }, info: null, loading: false, error: '', code: null, entering: false, redeem: '', confirmLeave: false, linked: false };

let build: (() => ReturnType<typeof OV.profileV>) | null = null;
const isOpen = (): boolean => modalOpen() && currentModal() === build;
const repaint = (): void => { if (isOpen()) repaintModal(); };

function isAccount(x: unknown): x is AccountInfo {
  return !!x && typeof x === 'object' && Array.isArray((x as { devices?: unknown }).devices);
}

/** Ответ сервера об аккаунте; устройство без имени берёт имя аккаунта (связали ноутбук — он уже «Павел») */
function setAccount(j: unknown): void {
  if (!isAccount(j)) return;
  prof.info = j;
  if (!ui.onlineName.trim() && j.name.trim()) {
    ui.onlineName = j.name.trim();
    rememberName();
    syncPresence();
    repaintSocial();
  }
}

/** Запрос к API аккаунта от имени этого устройства; ошибка сервера — текстом в окне */
async function call(path: string, extra: Record<string, string> = {}): Promise<unknown> {
  const r = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client: clientId(), key: deviceKey(), ...extra }),
  });
  const j: unknown = await r.json().catch(() => null);
  if (!r.ok) throw new Error((j as { error?: string } | null)?.error ?? `ошибка сервера (${r.status})`);
  return j;
}

async function run(what: () => Promise<void>): Promise<void> {
  prof.loading = true;
  prof.error = '';
  repaint();
  try {
    await what();
  } catch (e) {
    prof.error = e instanceof TypeError ? 'Нет связи с сервером — связывание работает только онлайн.' : e instanceof Error ? e.message : String(e);
  } finally {
    prof.loading = false;
    repaint();
  }
}

/** Ссылка в QR: открывает игру сразу с вводом этого кода */
export function linkUrl(code: string): string {
  return `${location.origin}${location.pathname}?link=${code}`;
}

/** QR-код ссылки svg-строкой (белый фон — камеры плохо читают инверсию) */
export function qrSvg(text: string): string {
  const q = qrcode(0, 'M');
  q.addData(text);
  q.make();
  return q.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
}

/**
 * Открыть профиль; tab — вкладка (по умолчанию — партии); enterCode — пришли по ссылке из QR: сразу ввод этого
 * кода на вкладке устройств (связывание — по кнопке)
 */
export function openProfile(tab: ProfileState['tab'] = 'games', enterCode?: string): void {
  Object.assign(prof, { tab: enterCode ? 'devices' : tab, error: '', code: null, confirmLeave: false, linked: false, entering: !!enterCode, redeem: enterCode ?? '' });
  // сервер ответил аккаунтом — значит, связь есть и связывать можно
  build = () => OV.profileV(prof, ui.onlineName, prof.info != null, st.push);
  openModal(build);
  if (prof.tab === 'devices') void loadPushDevices();
  // ачивки видны во вкладке «Партии» — уведомления о них прочитаны
  else markRead((i) => i.kind === 'ach');
  void run(async () => {
    setAccount(await call('api/account'));
  });
  void loadPlayer(prof.player, '', repaint);
}

export function profileTab(tab: ProfileState['tab']): void {
  prof.tab = tab;
  repaint();
  if (tab === 'devices') void loadPushDevices();
}

/** Партия из профиля (поражение или лучшая) — в плеер: owner — свой профиль или открытый чужой */
export function showGame(game: number, owner: 'me' | 'other'): void {
  const p = owner === 'me' ? prof.player.data : otherPlayer.data;
  const g = p ? [...p.losses, ...p.best].find((l) => l.game === game) : undefined;
  if (p && g) void startReplay(g, p.name, !!p.me);
}

/** Ачивки в профиле: только полученные ↔ все с условиями */
export function toggleAchAll(owner: 'me' | 'other'): void {
  const load = owner === 'me' ? prof.player : otherPlayer;
  load.achAll = !load.achAll;
  if (modalOpen()) repaintModal();
}

/** Другая страница поражений в профиле */
export function showLossPage(owner: 'me' | 'other', offset: number): void {
  pagePlayer(owner === 'me' ? prof.player : otherPlayer, offset);
}

export function linkShow(): void {
  prof.entering = false;
  prof.confirmLeave = false;
  void run(async () => {
    const j = (await call('api/link/new')) as { code?: unknown; expires?: unknown };
    if (typeof j.code !== 'string') throw new Error('сервер не выдал код');
    prof.code = { code: j.code, qr: qrSvg(linkUrl(j.code)), expires: Number(j.expires) * 1000 };
  });
}

export function linkCancel(): void {
  Object.assign(prof, { code: null, entering: false, confirmLeave: false, error: '' });
  repaint();
}

export function linkEnter(): void {
  prof.entering = true;
  prof.code = null;
  prof.confirmLeave = false;
  repaint();
}

export function linkRedeem(): void {
  const code = prof.redeem.trim();
  if (!code) {
    prof.error = 'Введи код со второго устройства';
    repaint();
    return;
  }
  void run(async () => {
    setAccount(await call('api/link/redeem', { code }));
    prof.entering = false;
    prof.redeem = '';
    prof.linked = true;
  });
}

export function linkLeave(confirmed: boolean): void {
  if (!confirmed) {
    prof.confirmLeave = true;
    repaint();
    return;
  }
  void run(async () => {
    setAccount(await call('api/link/leave'));
    prof.confirmLeave = false;
  });
}

/** Ввод кода в окне — в состояние (иначе перерисовка вернула бы поле к прежнему значению) */
export function initProfile(): void {
  if (typeof document === 'undefined') return;
  document.addEventListener('input', (e) => {
    const t = e.target;
    if (t instanceof HTMLInputElement && t.id === 'link-code') prof.redeem = t.value;
  });
  // пришли по ссылке из QR: открыть ввод кода и убрать код из адреса (перезагрузка не должна повторять)
  const u = new URL(location.href);
  const code = u.searchParams.get('link');
  if (code) {
    u.searchParams.delete('link');
    history.replaceState(history.state, '', u.href);
    openProfile('devices', code.toUpperCase());
  }
}
